import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import FAQSection from "@/components/FAQSection";
import Typewriter from "@/components/Typewriter";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Arrow120 } from "@/components/Arrow120";

const ServiceRow = ({ item, index }) => {
  const isReversed = index % 2 === 1;
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 80 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-120px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHovered(true)}
      onFocus={() => setHovered(true)}
      onTouchStart={() => setHovered(true)}
      data-testid={`service-row-${index}`}
      className={`group grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center py-16 md:py-28 ${
        isReversed ? "" : ""
      }`}
    >
      <motion.div
        initial={{ opacity: 0, x: isReversed ? 60 : -60 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
        className={`md:col-span-6 ${isReversed ? "md:col-start-7" : ""}`}
      >
        <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl bg-ahm-muted">
          <img
            src={item.image}
            alt={item.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-ahm-teal/40 to-transparent" />
          <span className="absolute top-6 left-6 font-mono text-xs uppercase tracking-[0.3em] text-ahm-cream bg-ahm-teal/85 px-3 py-1.5 rounded-full">
            {String(index + 1).padStart(2, "0")} · Service
          </span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: isReversed ? -60 : 60 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className={`md:col-span-6 ${isReversed ? "md:col-start-1 md:row-start-1" : ""}`}
      >
        <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-ahm-teal text-balance group-hover:text-ahm-orange transition-colors duration-500">
          {item.title}
        </h2>
        <div className="mt-8 min-h-[160px]">
          <Typewriter
            id={item.id}
            text={item.body}
            trigger={hovered}
            speed={14}
            className="text-base md:text-lg text-ahm-teal/85 leading-relaxed font-light"
          />
          {!hovered && (
            <p className="text-xs uppercase tracking-[0.25em] text-ahm-orange font-semibold mt-4">
              ↳ Hover to reveal
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const ServicesVideoCarousel = ({ videos }) => {
  const [idx, setIdx] = useState(0);
  if (!videos.length) return null;
  const visible = [0, 1, 2].map((o) => videos[(idx + o) % videos.length]);
  return (
    <section className="py-24 md:py-32 bg-ahm-teal text-ahm-cream" data-testid="services-video-carousel">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-ahm-yellow font-semibold mb-3">In motion</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">
              Our work, <em className="not-italic text-ahm-yellow">on play.</em>
            </h2>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setIdx((i) => (i - 1 + videos.length) % videos.length)}
              data-testid="services-video-prev"
              className="w-14 h-14 rounded-full border border-ahm-cream/30 hover:bg-ahm-orange hover:border-ahm-orange transition"
            >
              <ChevronLeft className="mx-auto" />
            </button>
            <button
              onClick={() => setIdx((i) => (i + 1) % videos.length)}
              data-testid="services-video-next"
              className="w-14 h-14 rounded-full border border-ahm-cream/30 hover:bg-ahm-orange hover:border-ahm-orange transition"
            >
              <ChevronRight className="mx-auto" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {visible.map((v, i) => (
            <div key={`${v.id}-${i}`} className="rounded-2xl overflow-hidden aspect-video bg-ahm-cream/10 shadow-2xl ring-1 ring-ahm-cream/15">
              <iframe
                src={`https://www.youtube.com/embed/${v.youtube_id}?autoplay=1&mute=1&loop=1&playlist=${v.youtube_id}&controls=0&modestbranding=1&rel=0`}
                title={v.title}
                className="w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const [items, setItems] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    api.get("/services_page").then((r) => setItems(r.data));
    api.get("/services_videos").then((r) => setVideos(r.data));
  }, []);

  return (
    <div data-testid="services-page">
      <section className="pt-28 md:pt-40 pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <p className="text-xs uppercase tracking-[0.3em] text-ahm-orange font-semibold mb-6">What we do</p>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] text-ahm-teal text-balance">
            Services that <em className="not-italic text-ahm-orange">show, not tell.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-ahm-teal/80 leading-relaxed">
            Six disciplines, one studio. Hover over any service to read the story behind it — line by line, the way we'd brief it.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 divide-y divide-ahm-teal/15">
          {items.map((it, i) => (
            <ServiceRow key={it.id} item={it} index={i} />
          ))}
        </div>
      </section>

      <ServicesVideoCarousel videos={videos} />

      <FAQSection page="services" />

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex flex-col md:flex-row gap-8 items-start md:items-center justify-between">
          <h3 className="font-display text-4xl md:text-5xl text-ahm-teal max-w-2xl">
            Found your service? <em className="not-italic text-ahm-orange">Let's get on a call.</em>
          </h3>
          <Link to="/contact" className="btn-pill group" data-testid="services-contact-cta">
            Start a project
            <Arrow120 />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
